import { useEffect, useMemo, useState } from 'react'
import { AppBskyFeedPost, AtUri } from '@atproto/api'
import { api } from '../api/client'
import { formatLocal, relativeTime } from '../utils/time'
import { blobUrl, bskyPostUrl, videoPlaylistUrl, videoThumbUrl } from '../utils/handle'
import { HlsVideo } from './HlsVideo'
import { ImageLightbox, type LightboxImage } from './ImageLightbox'

type Profile = {
  did: string
  handle: string
  displayName?: string
  avatar?: string
}

function blobLink(b: unknown): string | null {
  if (!b) return null
  if (typeof b === 'string') return b
  const o = b as { ref?: unknown }
  if (typeof o.ref === 'string') return o.ref
  if (o.ref && typeof o.ref === 'object') {
    const r = o.ref as { $link?: string }
    if (typeof r.$link === 'string') return r.$link
  }
  return null
}

type AnyEmbed = Record<string, unknown> & {
  $type?: string
  images?: { image?: unknown; alt?: string }[]
  external?: { uri?: string; title?: string; description?: string; thumb?: unknown }
  video?: unknown
  alt?: string
  media?: AnyEmbed
}

type PickedMedia = {
  images: { ref: string; alt?: string }[]
  external?: { uri: string; title?: string; description?: string; thumbRef?: string }
  video?: { ref: string; alt?: string }
}

function pickMedia(embed: AnyEmbed | undefined): PickedMedia {
  const out: PickedMedia = { images: [] }
  const visit = (e: AnyEmbed | undefined) => {
    if (!e) return
    const t = String(e.$type ?? '')
    if (t.startsWith('app.bsky.embed.images') && Array.isArray(e.images)) {
      for (const im of e.images) {
        const ref = blobLink(im.image)
        if (ref) out.images.push({ ref, alt: im.alt })
      }
    }
    if (t.startsWith('app.bsky.embed.external') && e.external) {
      const ex = e.external as {
        uri?: string
        title?: string
        description?: string
        thumb?: unknown
      }
      if (ex.uri) {
        out.external = {
          uri: ex.uri,
          title: ex.title,
          description: ex.description,
          thumbRef: blobLink(ex.thumb) ?? undefined,
        }
      }
    }
    if (t.startsWith('app.bsky.embed.video') && e.video) {
      const ref = blobLink(e.video)
      if (ref) out.video = { ref, alt: typeof e.alt === 'string' ? e.alt : undefined }
    }
    if (t.startsWith('app.bsky.embed.recordWithMedia') && e.media) {
      visit(e.media)
    }
  }
  visit(embed)
  return out
}

function OpenPostArrow({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="open post on Bluesky"
      className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-black/60 text-base text-white backdrop-blur transition hover:bg-black/80 hover:text-[color:var(--accent)]"
    >
      ↗
    </a>
  )
}

function ImageGrid({
  did,
  rkey,
  images,
  onOpen,
}: {
  did: string
  rkey: string
  images: { ref: string; alt?: string }[]
  onOpen: (img: LightboxImage) => void
}) {
  if (images.length === 0) return null
  const postHref = bskyPostUrl(did, rkey)

  function openImage(im: { ref: string; alt?: string }) {
    onOpen({
      src: blobUrl(did, im.ref, 'feed_fullsize'),
      alt: im.alt,
      postUrl: postHref,
      downloadName: `${rkey}-${im.ref.slice(-12)}.jpg`,
    })
  }

  if (images.length === 1) {
    const im = images[0]
    const thumb = blobUrl(did, im.ref, 'feed_thumbnail')
    return (
      <div className="mt-2 flex justify-center">
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-ink-900/40">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              openImage(im)
            }}
            className="block"
            title="open full image"
          >
            <img
              src={thumb}
              alt={im.alt ?? ''}
              loading="lazy"
              decoding="async"
              className="block max-h-[500px] max-w-full object-contain"
            />
          </button>
          <OpenPostArrow href={postHref} />
        </div>
      </div>
    )
  }

  return (
    <div className="mt-2 flex justify-center">
      <div className="grid w-full max-w-[560px] grid-cols-2 gap-2">
        {images.map((im, idx) => {
          const thumb = blobUrl(did, im.ref, 'feed_thumbnail')
          return (
            <div
              key={idx}
              className="relative overflow-hidden rounded-xl border border-white/10 bg-ink-900/40"
              style={{ aspectRatio: '1 / 1' }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  openImage(im)
                }}
                className="block h-full w-full"
                title="open full image"
              >
                <img
                  src={thumb}
                  alt={im.alt ?? ''}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </button>
              <OpenPostArrow href={postHref} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

function VideoPlayer({
  did,
  rkey,
  cid,
  alt,
}: {
  did: string
  rkey: string
  cid: string
  alt?: string
}) {
  return (
    <div className="mt-2 flex justify-center">
      <div className="relative w-full max-w-[560px] overflow-hidden rounded-xl border border-white/10 bg-black/40">
        <HlsVideo
          controls
          playsInline
          preload="metadata"
          poster={videoThumbUrl(did, cid)}
          className="aspect-video w-full bg-black"
          onClick={(e) => e.stopPropagation()}
          src={videoPlaylistUrl(did, cid)}
        />
        {alt ? <div className="px-3 py-1 text-[10px] text-ink-300">{alt}</div> : null}
        <OpenPostArrow href={bskyPostUrl(did, rkey)} />
      </div>
    </div>
  )
}

function ExternalCard({
  did,
  ext,
}: {
  did: string
  ext: NonNullable<PickedMedia['external']>
}) {
  let host = ext.uri
  try {
    host = new URL(ext.uri).host
  } catch {
    /* keep raw */
  }
  return (
    <div className="mt-2 flex justify-center">
      <a
        href={ext.uri}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="block w-full max-w-[560px] overflow-hidden rounded-xl border border-white/10 bg-white/5 transition hover:bg-white/10"
      >
        {ext.thumbRef ? (
          <img
            src={blobUrl(did, ext.thumbRef, 'feed_fullsize')}
            alt=""
            loading="lazy"
            className="aspect-video w-full object-cover"
          />
        ) : null}
        <div className="px-3 py-2">
          <div className="text-[10px] uppercase tracking-widest text-ink-400">{host}</div>
          {ext.title ? <div className="font-semibold">{ext.title}</div> : null}
          {ext.description ? <div className="text-xs text-ink-200">{ext.description}</div> : null}
        </div>
      </a>
    </div>
  )
}

function RawRecordDetails({ post }: { post: AppBskyFeedPost.Record }) {
  const [open, setOpen] = useState(false)
  return (
    <details
      className="rounded-lg bg-black/20 px-2 py-1"
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-ink-400">
        raw record
      </summary>
      {open ? (
        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all text-[10px] text-ink-300">
          {JSON.stringify(post, null, 2)}
        </pre>
      ) : null}
    </details>
  )
}

export function PostCard({
  uri,
  post,
  verb,
  verbedAt,
  defaultOpen = false,
}: {
  uri: string
  post: AppBskyFeedPost.Record
  verb?: 'liked' | 'reposted' | 'replied'
  verbedAt?: string
  defaultOpen?: boolean
}) {
  const at = useMemo(() => new AtUri(uri), [uri])
  const [profile, setProfile] = useState<Profile | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .profile(at.hostname)
      .then((data) => {
        if (cancelled || !data) return
        const link = blobLink(data.profile?.avatar)
        setProfile({
          did: data.did,
          handle: data.handle,
          displayName: data.profile?.displayName,
          avatar: link ? blobUrl(data.did, link, 'avatar') : undefined,
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [at.hostname])

  const postedDate = post.createdAt ? new Date(post.createdAt) : null
  const verbedDate = verbedAt ? new Date(verbedAt) : null
  const did = at.hostname
  const media = useMemo(() => pickMedia(post.embed as AnyEmbed | undefined), [post.embed])
  const hasMedia = media.images.length > 0 || !!media.video
  const isReply = !!post.reply?.parent
  const parentUri = post.reply?.parent?.uri
  const [lightbox, setLightbox] = useState<LightboxImage | null>(null)
  const postHref = bskyPostUrl(did, at.rkey)

  return (
    <details className="glass overflow-hidden" open={defaultOpen}>
      <summary className="cursor-pointer list-none px-4 py-3">
        <div className="flex items-start gap-3">
          {profile?.avatar ? (
            <img
              src={profile.avatar}
              alt=""
              loading="lazy"
              className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gradient-to-br from-sky-500/30 to-violet-500/30 ring-1 ring-white/10" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="truncate font-semibold">
                {profile?.displayName ?? profile?.handle ?? did}
              </span>
              {profile?.handle ? (
                <span className="truncate text-xs text-ink-300">@{profile.handle}</span>
              ) : null}
              {isReply ? (
                <span
                  className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[9px] uppercase tracking-widest text-ink-200"
                  title={parentUri ? `replying to ${parentUri}` : undefined}
                >
                  ↳ reply
                </span>
              ) : null}
            </div>
            {post.text ? (
              <p className="mt-1 whitespace-pre-wrap text-sm text-ink-100">{post.text}</p>
            ) : null}
          </div>
          <div className="flex max-w-[42%] flex-shrink-0 flex-col items-end gap-1 text-right">
            <a
              href={postHref}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[11px] accent-text hover:underline"
              title="open this post on Bluesky"
            >
              ↗ open in Bluesky
            </a>
            {verb && verbedDate ? (
              <div
                className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-widest"
                style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                title={`${verb} ${formatLocal(verbedDate)}`}
              >
                {verb} • {relativeTime(verbedDate)}
              </div>
            ) : null}
            {verb && verbedDate ? (
              <div className="text-[10px] text-ink-300" title={formatLocal(verbedDate)}>
                {formatLocal(verbedDate)}
              </div>
            ) : null}
            {postedDate ? (
              <>
                <div className="text-[10px] uppercase tracking-widest text-ink-400">
                  posted {relativeTime(postedDate)}
                </div>
                <div className="text-[10px] text-ink-200" title={postedDate.toISOString()}>
                  {formatLocal(postedDate)}
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Media is rendered in the summary so it's visible at-a-glance */}
        {media.images.length > 0 ? (
          <ImageGrid did={did} rkey={at.rkey} images={media.images} onOpen={setLightbox} />
        ) : null}
        {media.video ? (
          <VideoPlayer did={did} rkey={at.rkey} cid={media.video.ref} alt={media.video.alt} />
        ) : null}
        {media.external ? <ExternalCard did={did} ext={media.external} /> : null}
      </summary>

      <div className="space-y-2 border-t border-white/5 px-4 py-3 text-xs text-ink-300">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <a
            className="accent-text hover:underline"
            href={bskyPostUrl(did, at.rkey)}
            target="_blank"
            rel="noreferrer"
          >
            Open in Bluesky ↗
          </a>
          <span className="font-mono text-[10px] text-ink-400">{at.rkey}</span>
        </div>
        {hasMedia ? (
          <p className="text-[11px] text-ink-400">
            {media.images.length} image{media.images.length === 1 ? '' : 's'}
            {media.video ? ' • 1 video' : ''}
          </p>
        ) : null}
        <RawRecordDetails post={post} />
      </div>
      {lightbox ? <ImageLightbox image={lightbox} onClose={() => setLightbox(null)} /> : null}
    </details>
  )
}
